/**
 * Runs all three analyses and presents them together.
 *
 * PRD §4.5: showing WCA, RSS and Monte Carlo side by side is a feature, and
 * where they disagree the disagreement is the finding. The RSS/MC sigma ratio
 * is the cheapest available nonlinearity detector, and a WCA range narrower
 * than the observed Monte Carlo range means the worst-case box was clamped too
 * tightly to be believed.
 */

import type { CompiledModel } from './model.ts';
import { analyzeWorstCase, type WcaResult, type WcaOptions } from './wca.ts';
import { analyzeRss, type RssResult } from './rss.ts';
import {
  runMonteCarlo,
  type MonteCarloResult,
  type MonteCarloOptions,
  type OutputResult,
} from './montecarlo.ts';

export interface OutputComparison {
  outputId: string;
  wca: WcaResult;
  rss: RssResult;
  monteCarloMean: number;
  monteCarloStdDev: number;
  /** rss.stdDev / mc.stdDev. Far from 1 means the linearisation is not holding. */
  linearityRatio: number;
  /** True when RSS and MC agree within `linearityTolerance`. */
  linear: boolean;
  notes: string[];
}

export interface FullAnalysis {
  monteCarlo: MonteCarloResult;
  comparisons: OutputComparison[];
  seed: number;
  /** Everything a report needs to be reproducible (PRD §4.8). */
  provenance: {
    project: string;
    seed: number;
    iterations: number;
    engineVersion: string;
    timestamp: string;
  };
}

export const ENGINE_VERSION = '0.1.0';

/**
 * Assemble one output's comparison, including the notes shown as callouts.
 *
 * Exported because the app cannot call `analyze()`: its Monte Carlo comes back
 * from the worker pool, so it runs WCA and RSS itself and assembles the rest.
 * That previously meant a second, hand-maintained copy of the rules below, and
 * the two had already drifted — the app's copy and the engine's disagreed about
 * which notes exist. One function, called from both.
 *
 * A note here means *something about your model deserves attention*. Facts
 * about how a method works belong in `wca.notes`, and anything already stated
 * plainly on a stat card is not repeated as a callout — five warnings on a
 * correct model teach the reader to skip all five.
 */
export function compareOutput(
  spec: { id: string; lsl?: number; usl?: number },
  wca: WcaResult,
  rss: RssResult,
  mc: OutputResult,
  linearityTolerance = 0.05,
): OutputComparison {
  const ratio = mc.stats.stdDev > 0 ? rss.stdDev / mc.stats.stdDev : NaN;
  const linear = Number.isFinite(ratio) && Math.abs(ratio - 1) <= linearityTolerance;

  const notes: string[] = [];

  if (Number.isFinite(ratio) && !linear) {
    notes.push(
      `RSS sigma is ${(ratio * 100).toFixed(1)}% of the Monte Carlo sigma. The transfer ` +
        'function is not behaving linearly over these tolerances; trust Monte Carlo ' +
        'and treat the RSS contributor table as indicative only.',
    );
  }

  // Only a finding when clamping does not already explain it. An unbounded
  // distribution clamped for WCA *will* produce Monte Carlo samples outside the
  // worst-case range — that is arithmetic, not news, and wca.notes says so.
  if ((mc.stats.min < wca.min || mc.stats.max > wca.max) && wca.notes.length === 0) {
    notes.push(
      'Monte Carlo produced samples outside the worst-case range, and no distribution ' +
        'was clamped to explain it. Treat the worst-case limits as unreliable for this output.',
    );
  }

  // `matters`, not `rejected`: see the note on NormalityResult. At 10^5 samples
  // `rejected` fires on distributions that are normal to three decimal places.
  if (mc.normality.matters && (spec.lsl !== undefined || spec.usl !== undefined)) {
    notes.push(
      `Output is measurably non-normal (skew ${mc.stats.skewness.toFixed(3)}, excess ` +
        `kurtosis ${mc.stats.kurtosis.toFixed(3)}), enough to move the 3σ point by about ` +
        `${mc.normality.quantileShift.toFixed(2)}σ. Cp/Cpk assume a normal output; use the ` +
        'empirical yield instead.',
    );
  }

  // Deliberately no note for a low regression R². The Pareto chart raises that
  // itself, directly above the SRC column it qualifies, and says what it did
  // about it ("ranked by rank correlation instead"). Saying it here as well put
  // the same fact on screen twice, once in a place with no context.

  return {
    outputId: spec.id,
    wca,
    rss,
    monteCarloMean: mc.stats.mean,
    monteCarloStdDev: mc.stats.stdDev,
    linearityRatio: ratio,
    linear,
    notes,
  };
}

export interface AnalyzeOptions extends MonteCarloOptions {
  wca?: WcaOptions;
  /** Relative RSS/MC sigma gap tolerated before flagging nonlinearity. */
  linearityTolerance?: number;
}

export function analyze(model: CompiledModel, options: AnalyzeOptions): FullAnalysis {
  const seed = options.seed ?? model.project.seed;
  const tolerance = options.linearityTolerance ?? 0.05;

  const monteCarlo = runMonteCarlo(model, { ...options, seed });

  const comparisons: OutputComparison[] = model.outputs.map((out, i) => {
    const wca = analyzeWorstCase(model, i, { seed, ...options.wca });
    const rss = analyzeRss(model, i);
    const mc = monteCarlo.outputs[i];

    return compareOutput(out.spec, wca, rss, mc, tolerance);
  });

  return {
    monteCarlo,
    comparisons,
    seed,
    provenance: {
      project: model.project.name,
      seed,
      iterations: monteCarlo.completedIterations,
      engineVersion: ENGINE_VERSION,
      timestamp: new Date().toISOString(),
    },
  };
}
